import type { TFunction } from 'i18next';

import type {
    AppendFileTransferRequestDetailsPayload,
    CreateFileTransferRequestPayload,
    CreateFileTransferRequestResponse,
    UpdateFileTransferRequestResponse,
} from '../../../types/StudioFileManager';
import {
    executeAppendFileTransferRequestDetailsOnce,
    executeCreateFileTransferRequestOnce,
} from './grpcFileManagerApiHelpers';
import { UploadItem, UploadStatus } from './grpcFileManagerTypes';
import { applyCreateChunkToItems } from './grpcFileManagerUploadHelpers';

/**
 * Create/Append başına max dosya.
 * Postgre'de olmayan barkodlar Retail'e gider; tek istekte yüzlerce barkod HTTP timeout'a düşer.
 */
const CREATE_FILE_TRANSFER_CHUNK_SIZE = 40;

export type HandleCompleteDeps = {
    t: TFunction;
    uploadItems: UploadItem[];
    fileTransferRequestId: number | null;
    s3DestinationPath: string;
    selectedCountry: { id?: number } | null;
    selection: { shootingType: number | ''; mannequinId: number | '' };
    authorities: Array<{ titleId: number | ''; employeeId: number | '' }>;
    setFileTransferRequestId: (id: number | null) => void;
    setUploadItems: (items: UploadItem[]) => void;
    setUpdateResult: (result: UpdateFileTransferRequestResponse | null) => void;
    setStatus: (status: string) => void;
    setIsSelectionLoading: (loading: boolean) => void;
    setUploadStatusAccordionExpanded: (expanded: boolean) => void;
    setOngoingSubExpanded: (expanded: boolean) => void;
    setSuccessSubExpanded: (expanded: boolean) => void;
    setFailedSubExpanded: (expanded: boolean) => void;
    startUpload: (sourceOverride?: UploadItem[]) => Promise<boolean>;
    sendUpdateFileTransferRequest: (requestId: number) => Promise<void>;
};

function trimTrailingSlashes(value: string): string {
    let end = value.length;
    while (end > 0 && value[end - 1] === '/') {
        end -= 1;
    }
    return end === value.length ? value : value.slice(0, end);
}

function chunkArray<T>(items: T[], size: number): T[][] {
    if (size <= 0 || items.length === 0) {
        return items.length ? [items] : [];
    }
    const chunks: T[][] = [];
    for (let i = 0; i < items.length; i += size) {
        chunks.push(items.slice(i, i + size));
    }
    return chunks;
}

export async function sendUpdateForAllRequestIds(
    items: UploadItem[],
    sendUpdateFileTransferRequest: (requestId: number) => Promise<void>
): Promise<void> {
    const requestIds = [
        ...new Set(
            items
                .map((item) => item.fileTransferRequestId)
                .filter((id): id is number => typeof id === 'number' && id > 0)
        ),
    ];
    for (const requestId of requestIds) {
        await sendUpdateFileTransferRequest(requestId);
    }
}

function buildFileDtos(items: UploadItem[]) {
    return items.map((item) => ({
        originalFilePath: item.originalFilePath,
        originalFilename: item.rawFileName || item.fileName,
        storedFilePath: '',
        storedFilename: '',
        fileSize: item.totalSize,
        mimeType: item.file.type || 'application/octet-stream',
        isSuccess: false,
        retryCount: 0,
    }));
}

function buildCreatePayload(
    items: UploadItem[],
    deps: Pick<HandleCompleteDeps, 'selectedCountry' | 'selection' | 'authorities' | 's3DestinationPath'>
): CreateFileTransferRequestPayload {
    return {
        countryId: deps.selectedCountry?.id ?? 0,
        shootingTypeId: deps.selection.shootingType || 0,
        manequeenId: deps.selection.mannequinId || null,
        employees: deps.authorities.map((auth) => ({
            employeeTitleId: auth.titleId as number,
            employeeId: auth.employeeId as number,
        })),
        files: buildFileDtos(items),
        destinationFolderPath: deps.s3DestinationPath,
    };
}

function buildAppendPayload(requestId: number, items: UploadItem[]): AppendFileTransferRequestDetailsPayload {
    return {
        fileTransferRequestId: requestId,
        files: buildFileDtos(items),
    };
}

function markChunkHttpFailed(
    allItems: UploadItem[],
    chunkItems: UploadItem[],
    t: TFunction,
    requestId: number | null
): UploadItem[] {
    const chunkIds = new Set(chunkItems.map((item) => item.clientId));
    return allItems.map((item) => {
        if (!chunkIds.has(item.clientId)) {
            return item;
        }
        return {
            ...item,
            fileTransferRequestId: requestId ?? item.fileTransferRequestId,
            status: UploadStatus.Failed,
            errorMessage: t('studio.upload.uploadError'),
            progress: 0,
            storedFilename: undefined,
        };
    });
}

async function createFileTransfersInChunks(
    sourceItems: UploadItem[],
    itemsToCreate: UploadItem[],
    deps: HandleCompleteDeps,
    options?: { resetFailedMatchToPending?: boolean }
): Promise<{ items: UploadItem[]; requestIds: number[]; ok: boolean }> {
    const chunks = chunkArray(itemsToCreate, CREATE_FILE_TRANSFER_CHUNK_SIZE);
    let workingItems = sourceItems;
    let requestId = deps.fileTransferRequestId;

    for (let index = 0; index < chunks.length; index += 1) {
        const chunk = chunks[index];
        if (chunks.length > 1) {
            deps.setStatus(
                deps.t('studio.upload.createBatchProgress', {
                    current: index + 1,
                    total: chunks.length,
                })
            );
        }

        let response: CreateFileTransferRequestResponse | null = null;

        if (!requestId) {
            response = await executeCreateFileTransferRequestOnce(buildCreatePayload(chunk, deps), deps.t);
            if (!response?.fileTransferRequestId) {
                workingItems = markChunkHttpFailed(workingItems, chunk, deps.t, null);
                deps.setUploadItems(workingItems);
                continue;
            }
            requestId = response.fileTransferRequestId;
            deps.setFileTransferRequestId(requestId);
        } else {
            response = await executeAppendFileTransferRequestDetailsOnce(
                buildAppendPayload(requestId, chunk),
                deps.t
            );
            if (!response?.fileTransferRequestId) {
                workingItems = markChunkHttpFailed(workingItems, chunk, deps.t, requestId);
                deps.setUploadItems(workingItems);
                continue;
            }
        }

        workingItems = applyCreateChunkToItems(workingItems, chunk, response, deps.t, options);
        deps.setUploadItems(workingItems);
    }

    const hasUploadable = workingItems.some(
        (item) => !!item.storedFilename && item.fileTransferRequestDetailId != null
    );
    const requestIds = requestId ? [requestId] : [];
    return { items: workingItems, requestIds, ok: requestIds.length > 0 && (hasUploadable || itemsToCreate.length > 0) };
}

async function handleMatchFailedRetry(matchFailedItems: UploadItem[], deps: HandleCompleteDeps): Promise<void> {
    if (!trimTrailingSlashes(deps.s3DestinationPath || '')) {
        deps.setStatus(deps.t('studio.upload.selectDestination'));
        return;
    }

    deps.setIsSelectionLoading(true);
    deps.setUpdateResult(null);

    const {
        items: updatedItems,
        requestIds,
        ok,
    } = await createFileTransfersInChunks(deps.uploadItems, matchFailedItems, deps, {
        resetFailedMatchToPending: true,
    });

    deps.setIsSelectionLoading(false);

    if (!ok) {
        deps.setStatus(deps.t('studio.upload.uploadError'));
        return;
    }

    deps.setFileTransferRequestId(requestIds[0] ?? deps.fileTransferRequestId);

    const newPendingUploads = updatedItems.filter(
        (item) => item.status === UploadStatus.Pending && !!item.storedFilename && item.fileTransferRequestDetailId != null
    );

    if (newPendingUploads.length > 0) {
        const uploadSuccess = await deps.startUpload(updatedItems);
        if (uploadSuccess || updatedItems.some((item) => item.status === UploadStatus.Completed)) {
            await sendUpdateForAllRequestIds(updatedItems, deps.sendUpdateFileTransferRequest);
        }
        return;
    }

    await sendUpdateForAllRequestIds(updatedItems, deps.sendUpdateFileTransferRequest);
}

async function handlePrimaryCreateAndUpload(deps: HandleCompleteDeps): Promise<void> {
    deps.setIsSelectionLoading(true);
    deps.setUpdateResult(null);

    if (!trimTrailingSlashes(deps.s3DestinationPath || '')) {
        deps.setStatus(deps.t('studio.upload.selectDestination'));
        deps.setIsSelectionLoading(false);
        return;
    }

    const eligibleItems = deps.uploadItems.filter((item) => item.status !== UploadStatus.Failed);
    const {
        items: withTransferContext,
        requestIds,
        ok,
    } = await createFileTransfersInChunks(deps.uploadItems, eligibleItems, deps);

    if (!ok) {
        deps.setStatus(deps.t('studio.upload.uploadError'));
        deps.setIsSelectionLoading(false);
        return;
    }

    deps.setFileTransferRequestId(requestIds[0] ?? null);

    const acceptedCount = withTransferContext.filter((item) => !!item.storedFilename).length;
    const rejectedCount = withTransferContext.filter(
        (item) => item.status === UploadStatus.Failed && !item.storedFilename
    ).length;

    if (acceptedCount === 0) {
        deps.setUpdateResult({
            isSuccess: false,
            fileTransferRequestId: requestIds[0] ?? 0,
            totalFiles: withTransferContext.length,
            successfulFiles: 0,
            failedFiles: rejectedCount || withTransferContext.length,
            isCompleted: false,
        });
        deps.setStatus(
            deps.t('studio.upload.allCreateValidationsFailed', {
                failed: rejectedCount || withTransferContext.length,
            })
        );
        deps.setUploadStatusAccordionExpanded(true);
        deps.setOngoingSubExpanded(false);
        deps.setSuccessSubExpanded(false);
        deps.setFailedSubExpanded(true);
        deps.setIsSelectionLoading(false);
        return;
    }

    if (rejectedCount > 0) {
        deps.setStatus(
            deps.t('studio.upload.partialCreateValidationSummary', {
                success: acceptedCount,
                failed: rejectedCount,
            })
        );
    }

    deps.setIsSelectionLoading(false);
    const uploadSuccess = await deps.startUpload(withTransferContext);

    if (uploadSuccess || withTransferContext.some((item) => item.status === UploadStatus.Completed)) {
        await sendUpdateForAllRequestIds(withTransferContext, deps.sendUpdateFileTransferRequest);
        return;
    }

    if (rejectedCount > 0) {
        deps.setUpdateResult({
            isSuccess: false,
            fileTransferRequestId: requestIds[0] ?? 0,
            totalFiles: withTransferContext.length,
            successfulFiles: withTransferContext.filter((item) => item.status === UploadStatus.Completed).length,
            failedFiles: withTransferContext.filter((item) => item.status === UploadStatus.Failed).length,
            isCompleted: false,
        });
    }
}

export async function handleGrpcUploadComplete(deps: HandleCompleteDeps): Promise<void> {
    deps.setUploadStatusAccordionExpanded(true);
    deps.setOngoingSubExpanded(true);
    deps.setSuccessSubExpanded(false);
    deps.setFailedSubExpanded(true);

    const matchFailedItems = deps.uploadItems.filter(
        (item) => item.status === UploadStatus.Failed && !item.storedFilename
    );

    if (matchFailedItems.length > 0) {
        await handleMatchFailedRetry(matchFailedItems, deps);
        return;
    }

    const hasActualFailedUploads = deps.uploadItems.some(
        (item) => item.status === UploadStatus.Failed && !!item.storedFilename && item.fileTransferRequestDetailId != null
    );

    if (hasActualFailedUploads && deps.fileTransferRequestId) {
        deps.setUpdateResult(null);
        const uploadSuccess = await deps.startUpload();
        if (uploadSuccess) {
            await sendUpdateForAllRequestIds(deps.uploadItems, deps.sendUpdateFileTransferRequest);
        }
        return;
    }

    if (deps.uploadItems.some((item) => item.status === UploadStatus.Failed) && deps.fileTransferRequestId) {
        await sendUpdateForAllRequestIds(deps.uploadItems, deps.sendUpdateFileTransferRequest);
        return;
    }

    await handlePrimaryCreateAndUpload(deps);
}
